/**
 * FundraiserSelector.tsx - Fundraiser Selection Modal Component
 *
 * This component provides a modal interface for selecting one or multiple
 * fundraisers with search, filtering, and preview capabilities.
 *
 * Key Features:
 * - Multi-select fundraiser interface
 * - Search and filter functionality
 * - Fundraiser preview with key metrics
 * - Status-based filtering (active, completed, draft)
 * - Bulk selection and deselection
 * - Responsive modal design with pagination
 *
 * Selection Options:
 * - Single or multi-select modes
 * - Search by name, title, or description
 * - Filter by status, category, or date range
 * - Exclude specific fundraisers from selection
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - selectedFundraiserIds: string[] - Currently selected fundraiser IDs
 * - onSelectionChange: (ids: string[]) => void - Selection change handler
 * - excludeFundraiserId?: string - Fundraiser ID to exclude from selection
 * - multiSelect?: boolean - Enable multi-selection mode
 *
 * Dependencies: MobX store, portal rendering, fundraiser types
 */

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Search, X, Check } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  shouldShowExpirationBadge,
  getExpirationBadgeText,
  shouldShowPendingBadge,
  getPendingBadgeText,
} from "../../utils/badgesUtils";
import { FundRaiserStatus } from "../../enums";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 800px;
  height: 600px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 40px 40px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const ContentContainer = styled.div`
  padding: 24px 40px 0px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 20px;
  height: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const FundraiserGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  height: 300px;
  overflow-y: auto;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #ffffff;
  flex: 1;
`;

const FundraiserItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f8fafc;
  }
`;

const FundraiserImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const FundraiserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FundraiserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FundraiserMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FundraiserCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const FundraiserRadio = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

const PendingBadge = styled.div`
  background: rgba(59, 130, 246, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
`;

const ExpirationBadge = styled.div`
  background: rgba(220, 38, 38, 0.95);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 40px 32px;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
  margin-top: 20px;
`;

const ModalButton = styled.button<{ variant?: "primary" | "reset" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) => {
    if (props.variant === "primary") {
      return `
        background: #10b981;
        color: #ffffff;

        &:hover {
          background: #059669;
        }

        &:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }
      `;
    } else if (props.variant === "reset") {
      return `
        background: #ffffff;
        color: #10b981;
        border: 2px solid #10b981;

        &:hover {
          background: #f0fdf4;
          border-color: #059669;
          color: #059669;
        }

        &:disabled {
          background: #f8f9fa;
          color: #9ca3af;
          border-color: #e2e8f0;
          cursor: not-allowed;
        }
      `;
    } else {
      return `
        background: #ffffff;
        color: #64748b;
        border: 2px solid #e2e8f0;

        &:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
      `;
    }
  }}
`;

const EmbeddedContainer = styled.div`
  width: 100%;
`;

interface FundraiserSelectorProps {
  isModal?: boolean;
  isVisible?: boolean;
  onClose?: () => void;
  selectedFundraiserIds?: string[];
  onSelectionChange?: (fundraiserIds: string[]) => void;
  excludeFundraiserId?: string;
  status?: (keyof typeof FundRaiserStatus)[];
  availability?: ("current" | "future")[];
  multiple?: "no" | "yes";
}

export const FundraiserSelector: React.FC<FundraiserSelectorProps> = observer(
  ({
    isModal = false,
    isVisible = true,
    onClose,
    selectedFundraiserIds = [],
    onSelectionChange,
    excludeFundraiserId,
    status = [FundRaiserStatus.Active],
    availability = ["current", "future"],
    multiple = "yes",
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>(
      selectedFundraiserIds
    );

    // Update local state when props change
    useEffect(() => {
      setSelectedIds(selectedFundraiserIds);
    }, [selectedFundraiserIds]);

    const initialSelectionCount = selectedFundraiserIds.length;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModal && onClose) {
        onClose();
      }
    };

    useEffect(() => {
      if (isModal && isVisible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else if (isModal) {
        document.body.style.overflow = "unset";
      }

      return () => {
        if (isModal) {
          document.removeEventListener("keydown", handleKeyDown);
          document.body.style.overflow = "unset";
        }
      };
    }, [isModal, isVisible]);

    // Filter fundraisers based on status and availability
    const getFilteredFundraisers = () => {
      const today = new Date();
      const todayDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      return fundraiserStore.fundraisers.filter((fundraiser) => {
        // Exclude specific fundraiser if provided
        if (excludeFundraiserId && fundraiser.id === excludeFundraiserId) {
          return false;
        }

        // Filter by status
        if (!status.includes(fundraiser.status)) {
          return false;
        }

        // If status includes completed, inactive, or draft, ignore availability
        const ignoreAvailability = status.some((s) =>
          ["Completed", "Inactive", "Draft"].includes(s)
        );

        if (!ignoreAvailability && status.includes("Active")) {
          // Filter by availability for active fundraisers
          const startDate = new Date(fundraiser.startDate);
          const startDateOnly = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
          );

          const isCurrent = startDateOnly <= todayDate;
          const isFuture = startDateOnly > todayDate;

          const matchesAvailability =
            (availability.includes("current") && isCurrent) ||
            (availability.includes("future") && isFuture);

          if (!matchesAvailability) {
            return false;
          }
        }

        // Filter by search term
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          return (
            fundraiser.name.toLowerCase().includes(searchLower) ||
            fundraiser.title.toLowerCase().includes(searchLower)
          );
        }

        return true;
      });
    };

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const handleFundraiserToggle = (fundraiserId: string, checked: boolean) => {
      if (multiple === "no") {
        // For single selection, replace the selection
        setSelectedIds(checked ? [fundraiserId] : []);
      } else {
        // For multiple selection, add/remove from array
        const updated = checked
          ? [...selectedIds, fundraiserId]
          : selectedIds.filter((id) => id !== fundraiserId);
        setSelectedIds(updated);
      }
    };

    const handleReset = () => {
      setSelectedIds([]);
      onSelectionChange && onSelectionChange([]);
    };

    const clearSearch = () => {
      setSearchTerm("");
    };

    const handleCancel = () => {
      if (onClose) {
        onClose();
      }
    };

    const handleAdd = () => {
      if (onSelectionChange) {
        onSelectionChange(selectedIds);
      }
      if (onClose) {
        onClose();
      }
    };

    // Check if changes have been made
    const hasChanges = () => {
      return (
        JSON.stringify(selectedIds.slice().sort()) !==
        JSON.stringify(selectedFundraiserIds.slice().sort())
      );
    };

    // Get button text based on initial selection
    const getButtonText = () => {
      return initialSelectionCount > 0 ? "Update" : "Add";
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && isModal && onClose) {
        onClose();
      }
    };

    // Generate title based on select prop
    const getTitle = () => {
      return multiple === "no" ? "Select Fundraiser" : "Select Fundraisers";
    };

    // Generate subtitle based on status and availability
    const getSubtitle = () => {
      // If status includes completed, inactive, or draft, ignore availability
      const ignoreAvailability = status.some((s) =>
        ["Completed", "Inactive", "Draft"].includes(s)
      );

      if (ignoreAvailability) {
        if (status.length === 1) {
          return `Showing ${status[0]} fundraisers`;
        } else if (status.length === 2) {
          return `Showing ${status[0]} and ${status[1]} fundraisers`;
        } else if (status.length === 3) {
          return `Showing ${status[0]}, ${status[1]} and ${status[2]} fundraisers`;
        } else if (status.length >= 4) {
          return "Showing all fundraisers";
        }
      }

      // Handle active status with availability
      if (status.includes(FundRaiserStatus.Active) && status.length === 1) {
        if (
          availability.includes("current") &&
          availability.includes("future")
        ) {
          return "Showing active fundraisers";
        } else if (availability.includes("current")) {
          return "Showing active fundraisers that have started";
        } else if (availability.includes("future")) {
          return "Showing active fundraisers that have not yet started";
        }
      }

      return "Showing fundraisers";
    };

    const filteredFundraisers = getFilteredFundraisers();

    const renderContent = () => (
      <>
        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search fundraisers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus={isModal}
          />
          {searchTerm && (
            <ClearSearchButton onClick={clearSearch}>
              <X size={16} />
            </ClearSearchButton>
          )}
        </SearchContainer>

        <FundraiserGrid>
          {filteredFundraisers.map((fundraiser) => (
            <FundraiserItem key={fundraiser.id}>
              {multiple === "no" ? (
                <FundraiserRadio
                  type="radio"
                  name="fundraiser-selection"
                  checked={selectedIds.includes(fundraiser.id)}
                  onChange={(e) =>
                    handleFundraiserToggle(fundraiser.id, e.target.checked)
                  }
                />
              ) : (
                <FundraiserCheckbox
                  type="checkbox"
                  checked={selectedIds.includes(fundraiser.id)}
                  onChange={(e) =>
                    handleFundraiserToggle(fundraiser.id, e.target.checked)
                  }
                />
              )}
              {fundraiser.getPrimaryImageToShow() && (
                <FundraiserImage
                  src={
                    fundraiser.getPrimaryImageToShow()
                  }
                  alt={fundraiser.name}
                />
              )}
              <FundraiserInfo>
                <FundraiserName>
                  {fundraiser.title || fundraiser.name}
                </FundraiserName>
                <FundraiserMeta>
                  {shouldShowPendingBadge(
                    fundraiser.startDate,
                    fundraiser.status
                  ) && (
                      <PendingBadge>
                        {getPendingBadgeText(new Date(fundraiser.startDate))}
                      </PendingBadge>
                    )}
                  {!shouldShowPendingBadge(
                    fundraiser.startDate,
                    fundraiser.status
                  ) &&
                    shouldShowExpirationBadge(
                      fundraiser.endDate,
                      fundraiser.status
                    ) && fundraiser.endDate && (
                      <ExpirationBadge>
                        {getExpirationBadgeText(new Date(fundraiser.endDate))}
                      </ExpirationBadge>
                    )}
                  {fundraiser.totalRaisedAmount > 0 && (
                    <span>
                      {formatCurrency(fundraiser.totalRaisedAmount)} raised
                      {fundraiser.goal > 0 &&
                        ` (${Math.round((fundraiser.totalRaisedAmount / fundraiser.goal) * 100)}%)`}
                    </span>
                  )}
                </FundraiserMeta>
              </FundraiserInfo>
            </FundraiserItem>
          ))}

          {filteredFundraisers.length === 0 && (
            <div
              style={{
                gridColumn: "1 / -1",
                padding: "24px",
                textAlign: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              {searchTerm
                ? `No fundraisers found matching "${searchTerm}"`
                : "No fundraisers available"}
            </div>
          )}
        </FundraiserGrid>

        {isModal && (
          <ModalActions>
            <ModalButton onClick={handleCancel}>Cancel</ModalButton>
            <ModalButton
              variant="reset"
              onClick={handleReset}
              disabled={selectedIds.length === 0}
            >
              Reset
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={handleAdd}
              disabled={!hasChanges()}
            >
              {getButtonText()}
            </ModalButton>
          </ModalActions>
        )}
      </>
    );

    if (isModal) {
      return createPortal(
        <ModalOverlay visible={isVisible || false} onClick={handleOverlayClick}>
          <ModalContent>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>

            <ModalHeader>
              <ModalTitle>{getTitle()}</ModalTitle>
              <ModalSubtitle>{getSubtitle()}</ModalSubtitle>
            </ModalHeader>

            <ContentContainer>{renderContent()}</ContentContainer>
          </ModalContent>
        </ModalOverlay>,
        document.body
      );
    }

    return <EmbeddedContainer>{renderContent()}</EmbeddedContainer>;
  }
);
